// Injectable-alarm controls (Live Floor). The sim action routes are same-origin and not auth-gated,
// so these are plain relative fetches. Company must be passed through — an omitted ?company= falls back
// to 'default' on the server (the documented multi-tenant landmine).
const BASE = '/machinesim/api/sim';

async function post(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

// Real (level, code, message) seeds captured off the MM fleet — for the picker.
export async function getAlarmCatalog() {
  const res = await fetch(`${BASE}/alarm-catalog`);
  const json = await res.json().catch(() => ({}));
  return Array.isArray(json.alarms) ? json.alarms : [];
}

// Inject any alarm — catalog entry or fully custom { code, message, level }. FAULT halts the machine
// (shows in MM as ALARM state + message); WARNING is non-halting (message only).
export function injectAlarm(machine, { code, message, level }, company) {
  return post(`${BASE}/${encodeURIComponent(machine)}/inject-alarm?company=${encodeURIComponent(company || 'default')}`,
    { code, message, level });
}

export function clearAlarm(machine, company) {
  return post(`${BASE}/${encodeURIComponent(machine)}/clear-alarm?company=${encodeURIComponent(company || 'default')}`, {});
}

// Operator "change this tool" request from the Live Floor — queues a next-boundary tool change for one
// turret slot (the trigger side; distinct from the /tool-change completion endpoint). The engine drops
// the machine into its normal tool-change hold + 300x alarm on the next completed part.
export function requestToolChange(machine, toolKey, company) {
  return post(`${BASE}/${encodeURIComponent(machine)}/tool-change-request?company=${encodeURIComponent(company || 'default')}`,
    { toolKey });
}

// Operator "the change is done, resume now" — the COMPLETION side. Resets every tool currently due
// (the counter drop TI detects) and ends the tool-change hold immediately instead of waiting out the
// remaining downtime, which is what makes a simulation run reach the next interesting event without
// sitting through the timer. No positions = all due tools, matching the hold the machine is in.
export function finishToolChange(machine, company) {
  return post(`${BASE}/${encodeURIComponent(machine)}/tool-change?company=${encodeURIComponent(company || 'default')}`, {});
}

// Operator "setup is done, start cutting" — ends the SETUP hold early. The changeover twin of
// finishToolChange: setup is a timed phase modelling the real changeover, and a simulation run
// usually wants the cut rather than the wait.
export function finishSetup(machine, company) {
  return post(`${BASE}/${encodeURIComponent(machine)}/finish-setup?company=${encodeURIComponent(company || 'default')}`, {});
}
